import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button } from '@mui/material';
import { RemoveRedEyeOutlined, DeleteOutline } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import withComponentAuth from '../../hoc/withComponentAuth';
import { SmartFilter } from '../../components/common/SmartFilter';
import Pagination from '../../components/atoms/Pagination';
import { ListTable } from '../../components/molecules/ListTable';
import { GETTechnicians, BulkDeleteTechnicians } from '../../services/technician';
import { ITechnician } from '../../utils/entities/interface';
import {
  capitalizeWords,
  debounce,
  formatDate,
  formatTechnicianType,
} from '../../utils/helper';
import { APP_SHELL_HEADER_CONTROL_HEIGHT } from '../../components/layout/layoutConstants';
import { useAdminPermission } from '../../hooks/useAdminPermission';
import { DialogBoxComp } from '../../components/atoms/DialogBox';
import useSnackbar from '../../hooks/useSnackbar';

const newJoinHeaderCells: [string, number][] = [
  ['Name', 100],
  ['Contact Number', 100],
  ['DOB', 80],
  ['Type', 70],
  ['Status', 70],
  ['Action', 50],
];

const getTechnicianKycStatus = (technician: ITechnician) => {
  const status =
    technician.kycStatus ||
    (technician as any).status ||
    (technician as any).verificationStatus ||
    'Pending';

  return String(status);
};

const isNewJoinTechnician = (technician: ITechnician) => {
  const kycStatus = getTechnicianKycStatus(technician).trim().toUpperCase();

  // New Join = not yet KYC verified. Once a technician is VERIFIED they're
  // done with this pipeline — position/status being unset shouldn't pull
  // them back into this list.
  return kycStatus !== 'VERIFIED';
};

const NewJoinTechnicians = () => {
  const navigate = useNavigate();

  const [page, setPage] = useState<number>(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [allTechnicians, setAllTechnicians] = useState<ITechnician[]>([]);
  const [totalTechnician, setTotalTechnician] = useState(0);
  const [searchQuery, setSearchQuery] = useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  const { canDelete } = useAdminPermission();
  const { SnackbarComponent, showSnackbar } = useSnackbar();
  const hasDeletePermission = canDelete('technicians');

  useEffect(() => {
    fetchTechnicians(searchQuery);
  }, [searchQuery]);

  const fetchTechnicians = async (searchQuery?: string) => {
    try {
      setIsLoading(true);

      const response = await GETTechnicians({
        page: 1,
        limit: 10000,
        searchQuery,
        sortField: 'joiningDate',
        sortOrder: 'DESC',
      });

      const filteredTechnicians = (response.data || []).filter(
        (technician: ITechnician) => isNewJoinTechnician(technician),
      );

      setAllTechnicians(filteredTechnicians);
      setTotalTechnician(filteredTechnicians.length);
    } catch (error) {
      console.error('Failed to fetch new join technicians:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const technicians = useMemo(() => {
    const startIndex = (page - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;

    return allTechnicians.slice(startIndex, endIndex);
  }, [allTechnicians, page, rowsPerPage]);

  const handleChangePage = (_event: any, newPage: number) => {
    setPage(newPage + 1);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  const handleSearchRefresh = debounce((search: string) => {
    setSearchQuery(search);
    setPage(1);
  }, 500);

  const handleDeleteSelected = async () => {
    if (selectedIds.length === 0) return;

    try {
      setIsDeleting(true);
      const result = await BulkDeleteTechnicians(selectedIds);
      showSnackbar(`${result.deletedCount} technician(s) deleted successfully`, 'success');
      setSelectedIds([]);
      setOpenDeleteModal(false);
      fetchTechnicians(searchQuery);
    } catch (error: any) {
      showSnackbar(error?.message || 'Failed to delete technicians', 'error');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        maxHeight: '100%',
      }}
    >
      {SnackbarComponent}
      <SmartFilter
        title="New Join Technicians"
        createFilter={['SEARCH']}
        addDrawerSize="lg"
        filterdrawerSize="sm"
        filterHeader="Filter New Join"
        addHeader="New Join"
        onSearch={handleSearchRefresh}
        extraActions={
          hasDeletePermission && selectedIds.length > 0 ? (
            <Button
              variant="contained"
              color="primary"
              startIcon={<DeleteOutline />}
              onClick={() => setOpenDeleteModal(true)}
              sx={{
                textTransform: 'none',
                borderRadius: 2,
                height: APP_SHELL_HEADER_CONTROL_HEIGHT,
                flexShrink: 0,
                whiteSpace: 'nowrap',
              }}
            >
              Delete ({selectedIds.length})
            </Button>
          ) : null
        }
      />

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
          p: 1,
        }}
      >
      <Box
        sx={{
          height: '100%',
          maxHeight: '100%',
          overflowY: 'scroll',
          scrollbarWidth: 'none',
        }}
      >
        <ListTable
          headerCells={newJoinHeaderCells}
          rows={technicians}
          isLoading={isLoading}
          noDataText="No New Join Technicians Found"
          showCheckbox={hasDeletePermission}
          selectedIds={selectedIds}
          onSelectionChange={setSelectedIds}
          actions={[
            {
              icon: <RemoveRedEyeOutlined />,
              onClick: (technician: ITechnician) =>
                navigate(`/technicians/${technician._id}`),
            },
          ]}
          renderCell={(technician: ITechnician, colIndex: number) => {
            switch (colIndex) {
              case 0:
                return capitalizeWords(technician.name) || 'N/A';

              case 1:
                return technician.phoneNumber
                  ? `${technician.countryCode || '+91'}-${technician.phoneNumber.slice(0, 10)}`
                  : 'N/A';

              case 2:
                return formatDate((technician as any).dob) || 'N/A';

              case 3:
                return formatTechnicianType(technician.type);

              case 4: {
                const status = getTechnicianKycStatus(technician);

                return (
                  <Box
                    component="span"
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      px: 1.8,
                      py: 0.8,
                      minWidth: '90px',
                      border: '1.5px solid #7B1FA2',
                      borderRadius: '9px',
                      color: '#9E9E9E',
                      fontSize: '13px',
                      fontWeight: 400,
                      lineHeight: 1,
                      cursor: 'default',
                      pointerEvents: 'none',
                      userSelect: 'none',
                      backgroundColor: 'transparent',
                    }}
                  >
                    {capitalizeWords(status)}
                  </Box>
                );
              }

              default:
                return null;
            }
          }}
        />
      </Box>

      <Box display="block" bottom={0} right={0}>
        <Pagination
          count={totalTechnician}
          page={page - 1}
          rowsPerPage={rowsPerPage}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Box>

      <DialogBoxComp
        open={openDeleteModal}
        title="Delete Technicians"
        content={`Are you sure you want to delete ${selectedIds.length} technician(s)? This action will disable the selected technician accounts.`}
        handleClose={() => setOpenDeleteModal(false)}
        handleConfirm={handleDeleteSelected}
      />
      </Box>
    </Box>
  );
};

export default withComponentAuth(NewJoinTechnicians);
